import { Alert } from '@/global/components/Alert'
import HorizontalDivider from '@/global/components/Divider/HorizontalDivider'
import { Link } from '@/global/components/Link'
import Grid from '@/global/components/ui/Grid'
import { hasPermission } from '@/hooks/useBackendMenu'
import { SN } from '@acme/core'
import {
    FormDatePicker,
    FormField,
    FormFieldCheckbox,
    FormFieldTime,
} from 'global/components/Form'
import IconeAjuda from 'global/components/IconeAjuda'
import { useFormContext } from 'react-hook-form'
import HorasExtrasPart from './HorasExtras'
import FormHoristaFaltas from './HoristaFaltas'
import FormNoturnoRegraCalculo from './Noturno'
import PorcentagemPart from './Porcentagem'

export function TabGeral() {
    const { watch, setValue } = useFormContext()

    const compensacaoManual = watch('compensacaoManual')
    const bancoHoras = watch('controlarBancoHoras')
    const controlaBancoHoras = SN.isTrue(bancoHoras)
    const hrsExtrasNoturnas = watch('hrsExtrasNoturnas')

    return (
        <Grid container className="pb-8">
            <Grid item xs={12}>
                <HorizontalDivider>Dados básicos</HorizontalDivider>
            </Grid>
            <Grid item xs={12}>
                <FormField
                    name="nome"
                    label="Nome da regra de cálculo"
                    autoComplete="off"
                    required
                />
            </Grid>

            <Grid item xs={12}>
                <HorizontalDivider>Banco de horas</HorizontalDivider>
            </Grid>
            <Grid item xs={12}>
                <FormFieldCheckbox
                    name="controlarBancoHoras"
                    label="Controla banco de horas"
                    disabled={compensacaoManual}
                    onChange={checked => {
                        if (checked) setValue('compensacaoFifo', false)
                    }}
                    endAdornment={
                        <IconeAjuda>
                            Caso habilitado, o saldo de horas do colaborador
                            será zerado apenas no fechamento do banco de horas.
                            <br />
                            <br />O período de fechamento do banco de horas é
                            configurado na tela de configurações da empresa.
                            <br />
                            <br />
                            <strong>Atenção:</strong> Esta opção não pode ser
                            habilitada junto com a compensação manual.
                        </IconeAjuda>
                    }
                />
            </Grid>
            <Grid item sm={6} xs={12}>
                <FormDatePicker
                    name="inicioBancoHoras"
                    label="Início do banco de horas"
                    disabled={!controlaBancoHoras}
                    type="month"
                    endAdornment={
                        <IconeAjuda>
                            Competência a partir da qual o banco de horas passa
                            a valer. O mês informado já acumula.
                            <br />
                            <br />
                            Deixe em branco se o banco sempre valeu.
                        </IconeAjuda>
                    }
                    helperText="Deixe em branco para indeterminado"
                />
            </Grid>
            <Grid item sm={6} xs={12}>
                <FormDatePicker
                    name="fimBancoHoras"
                    label="Fim do banco de horas"
                    type="month"
                    disabled={!controlaBancoHoras}
                    endAdornment={
                        <IconeAjuda>
                            Última competência em que o banco de horas vale. O
                            mês informado ainda acumula; o seguinte já não.
                            <br />
                            <br />O saldo remanescente não é pago
                            automaticamente — use o pagamento de horas na
                            competência do fim.
                        </IconeAjuda>
                    }
                />
            </Grid>

            <Grid item xs={12} className="mt-2">
                <HorizontalDivider>Compensações</HorizontalDivider>
            </Grid>

            <Grid item sm={6} xs={12}>
                <FormFieldCheckbox
                    name="compensacaoDeHoras"
                    label="Compensação diária"
                    endAdornment={
                        <IconeAjuda>
                            Caso habilitado, as faltas e horas extras que
                            ocorrerem no mesmo dia serão compensadas e o dia
                            nunca vai apresentar faltas e horas extras ao mesmo
                            tempo.
                            <br />
                            <br />
                            Para controlar as compensações entre vários dias,
                            configure a porcentagem da regra de cálculo.
                        </IconeAjuda>
                    }
                />
            </Grid>
            <Grid item sm={6} xs={12}>
                <FormFieldCheckbox
                    name="compensacaoManual"
                    label="Compensação manual"
                    disabled={controlaBancoHoras}
                    endAdornment={
                        <IconeAjuda>
                            Caso habilitado, o sistema irá compensar manualmente
                            as horas do colaborador.
                            <br />
                            <br />
                            Se a sua empresa não faz o controle de compensações
                            manuais, mantenha a opção desabilitada.
                            <br />
                            <br />
                            <strong>Atenção:</strong> Esta opção não pode ser
                            habilitada junto com o controle de banco de horas.
                        </IconeAjuda>
                    }
                />
            </Grid>
            <Grid item sm={6} xs={12}>
                <FormFieldCheckbox
                    name="compensarJustificadas"
                    label="Compensar faltas justificadas"
                    endAdornment={
                        <IconeAjuda>
                            Caso desabilitado, o sistema não irá compensar
                            faltas justificadas, utilize esta opção caso sua
                            empresa faz a separação entre faltas justificadas e
                            injustificadas.
                        </IconeAjuda>
                    }
                />
            </Grid>
            <Grid item sm={6} xs={12}>
                <FormFieldCheckbox
                    name="compensarInjustificadas"
                    label="Compensar faltas injustificadas"
                    endAdornment={
                        <IconeAjuda>
                            Caso desabilitado, o sistema não irá compensar
                            faltas injustificadas, utilize esta opção caso sua
                            empresa faz a separação entre faltas justificadas e
                            injustificadas.
                        </IconeAjuda>
                    }
                />
            </Grid>
            <Grid item sm={6} xs={12}>
                <FormFieldCheckbox
                    name="hrsExtrasNoturnas"
                    label="Calcular horas extras noturnas"
                    onChange={checked => {
                        if (!checked) {
                            setValue('compensarHrsExtrasNoturnas', false)
                        }
                    }}
                    endAdornment={
                        <IconeAjuda>
                            Caso habilitado, o sistema irá calcular e registrar
                            separadamente as horas extras realizadas no período
                            noturno (entre o início e fim do horário noturno
                            configurado na empresa).
                        </IconeAjuda>
                    }
                />
            </Grid>
            <Grid item sm={6} xs={12}>
                <FormFieldCheckbox
                    name="compensarHrsExtrasNoturnas"
                    label="Compensar horas extras noturnas"
                    disabled={!hrsExtrasNoturnas}
                    endAdornment={
                        <IconeAjuda>
                            Caso habilitado, as horas extras noturnas serão
                            compensadas com faltas no banco de horas. Caso
                            desabilitado, serão apenas exportadas para a folha
                            de pagamento.
                            <br />
                            <br />
                            Só pode ser habilitado junto com{' '}
                            <b>Calcular horas extras noturnas</b>
                        </IconeAjuda>
                    }
                />
            </Grid>
            <Grid item sm={6} xs={12}>
                <FormFieldCheckbox
                    name="toleranciaCLT"
                    label="Tolerância CLT"
                    endAdornment={
                        <IconeAjuda>
                            Caso habilitado, o sistema irá considerar a
                            tolerância conforme a CLT para os registros de
                            ponto, independente da configuração de tabela de
                            horário.
                            <br />
                            <br />O parágrafo primeiro do art. 58 da CLT cita
                            que: "não serão descontadas nem computadas como
                            jornada extraordinária as variações de horário no
                            registro de ponto não excedentes de cinco minutos,
                            observado o limite máximo de dez minutos diários."
                        </IconeAjuda>
                    }
                />
            </Grid>
            <Grid item sm={6} xs={6}>
                <FormFieldCheckbox
                    name="compensacaoFifo"
                    label="Compensação cronológica"
                    disabled={!bancoHoras}
                    endAdornment={
                        <IconeAjuda>
                            Caso habilitado, a compensação de faltas e o
                            pagamento de horas consomem o saldo do banco na
                            ordem cronológica (o dia mais antigo primeiro —
                            PEPS), em vez de priorizar a maior ou menor
                            porcentagem. O dia exato que compensou cada saldo
                            aparece no{' '}
                            {hasPermission(
                                ['read', 'VencimentoDoBancoDeHoras'],
                                () => (
                                    <Link href="/relatorios/vencimento-banco">
                                        relatório de vencimento do banco de
                                        horas
                                    </Link>
                                ),
                                () =>
                                    'relatório de vencimento do banco de horas',
                            )}
                            .
                            <br />
                            <br />
                            <strong>Atenção:</strong> disponível apenas com o
                            controle de banco de horas habilitado.
                        </IconeAjuda>
                    }
                />
            </Grid>

            <Grid item xs={12} className="mt-2">
                <HorizontalDivider>Interjornada</HorizontalDivider>
            </Grid>
            <Grid item sm={6} xs={12}>
                <FormFieldCheckbox
                    name="calcularHorasSuprimidas"
                    label="Calcular horas suprimidas"
                    endAdornment={
                        <IconeAjuda>
                            Caso habilitado, o sistema irá calcular e exibir as
                            horas suprimidas no espelho de ponto e relatório de
                            horas suprimidas.
                            <br />
                            <br />
                            Quando desabilitado, o relatório de inconsistências
                            ainda vai exibir as horas suprimidas.
                        </IconeAjuda>
                    }
                />
            </Grid>
            <Grid item xs={6}>
                <FormFieldTime
                    name="horasInterjornada"
                    label="Horas interjornada"
                    required
                    endAdornment={
                        <div className="inline-flex items-baseline">
                            <span>h</span>
                            <IconeAjuda>
                                Determina o intervalo mínimo entre a jornada de
                                trabalho de um dia para outro. Horas inferiores
                                a configuração serão consideradas como horas
                                suprimidas.
                                <br />
                                <br />A CLT determina um intervalo mínimo de 11
                                horas entre uma jornada e outra.
                            </IconeAjuda>
                        </div>
                    }
                />
            </Grid>
        </Grid>
    )
}

export function TabNoturno() {
    return <FormNoturnoRegraCalculo />
}

export function TabHorasExtras() {
    const {
        formState: { errors: formErrors },
    } = useFormContext()

    return (
        <Grid container columns={30}>
            <Grid item xs={30}>
                <HorizontalDivider className="mt-2 mb-4">
                    Definições de porcentagens para compensação
                </HorizontalDivider>
            </Grid>
            <Grid item xs={30}>
                <ErrorAlert obj={formErrors} field="porcentagens" />
            </Grid>
            <Grid item xs={30}>
                <PorcentagemPart />
            </Grid>

            <Grid item xs={30}>
                <HorizontalDivider className="mt-2 mb-4">
                    Configurações de horas extras semanais
                </HorizontalDivider>
            </Grid>
            <Grid item xs={30}>
                <ErrorAlert obj={formErrors} field="horasExtras" />
            </Grid>
            <Grid item xs={30}>
                <HorasExtrasPart />
            </Grid>
        </Grid>
    )
}

export function TabHoristaEFaltas() {
    return <FormHoristaFaltas />
}

interface IErrorProps {
    obj: Record<string, any>
    field: string
}

function ErrorAlert({ obj, field }: IErrorProps) {
    if (!obj[field]) return null

    const msgRoot = obj[field].root?.message || obj[field].message?.toString()
    if (msgRoot) return <Alert severity="error">{msgRoot}</Alert>

    let msg = obj[field]
    do {
        if (!msg) return null
        if ('message' in msg) msg = String(msg.message)
        else if (Array.isArray(msg)) msg = msg.find(Boolean)
        else msg = Object.values(msg)
    } while (typeof msg !== 'string')

    return <Alert severity="error">{msg}</Alert>
}
